/**
 * Keep keyboard focus inside a modal surface while it is open.
 *
 * - On activation, focus moves into the container: to `initialFocus()` if it
 *   returns an element, else the first focusable element, else the container.
 * - Tab / Shift+Tab wrap at the ends instead of escaping to the page behind.
 * - Tab while focus sits in another surface outside the container (Privy's sign-in or signing
 *   dialog, opened over ours) is left alone, so that surface stays keyboard-usable. Focus that
 *   was lost to the page (nothing focused) is pulled back in.
 * - On deactivation (or unmount), focus returns to whatever had it before,
 *   usually the control that opened the surface.
 *
 * No dependency: the app has no focus-trap library, and the rules here are the
 * small subset the mobile menu needs. Dialogs use ModalShell, which has its own trap.
 */
import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Hidden by an ancestor in a way that also removes it from the tab order. */
const HIDDEN_ANCESTOR = '[hidden],[inert],[aria-hidden="true"],.hidden,.invisible';

/** Not rendered or not visible (display: none on it or an ancestor, or visibility: hidden). */
function isHiddenByStyle(el: HTMLElement, container: HTMLElement): boolean {
  if (getComputedStyle(el).visibility === 'hidden') return true;
  for (let node: HTMLElement | null = el; node && node !== container; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return true;
  }
  return false;
}

/**
 * Elements Tab can reach, in document order. A native control with tabindex="-1" matches the
 * selectors but is out of the tab order, and an element hidden by class or style cannot take
 * focus: either one at an end would let Tab escape before the wrap applies.
 */
export function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.tabIndex >= 0 && !el.closest(HIDDEN_ANCESTOR) && !isHiddenByStyle(el, container)
  );
}

export interface FocusTrapOptions {
  /** Element to focus first; falls back to the first focusable element. */
  initialFocus?: () => HTMLElement | null;
  /** Return focus to the previously focused element on close (default true). */
  restoreFocus?: boolean;
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  options: FocusTrapOptions = {}
): void {
  // Read the latest options without re-running the trap (which would steal focus again).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const initial = optionsRef.current.initialFocus?.() ?? getFocusable(container)[0] ?? container;
    if (initial === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    initial.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const active = document.activeElement;
      const lost = !active || active === document.body;
      // Focus is in someone else's dialog: not ours to move.
      if (!lost && !container.contains(active)) return;
      const items = getFocusable(container);
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = active;
      if (e.shiftKey && (current === first || current === container || lost)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || lost)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (
        optionsRef.current.restoreFocus !== false &&
        previouslyFocused &&
        previouslyFocused.isConnected
      ) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef]);
}
